// src/cockpit4/components/ContentDistributor.tsx
// ============================================
// P0-功能3：一键多平台分发（媒体工作室）
//
// 功能：
//   1. 多平台选择（公众号/小红书/抖音/B站/知乎）
//   2. 每个平台的预览和格式适配
//   3. 立即发布 + 定时发布
//   4. 发布状态实时追踪
//   5. 发布失败自动重试
//   6. 发布后数据自动回传（占位，待平台 API 接入）
//
// 数据来源：
//   - MediaStudio.list_packages（已生成的媒体包）
//   - Vault 中发布记录
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  Eye,
  ExternalLink,
  Calendar,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import type { MediaPackage } from "../../runtime/MediaStudio/types";

// ============================================
// 类型定义
// ============================================

type PublishStatus = "pending" | "publishing" | "success" | "failed" | "scheduled";

interface PublishTarget {
  platform: string;
  label: string;
  color: string;
  /** 格式适配说明 */
  formatAdaptation: string;
  /** 字数限制 */
  charLimit: number;
  selected: boolean;
  status: PublishStatus;
  /** 发布时间 */
  publishedAt?: string;
  /** 失败原因 */
  error?: string;
  /** 重试次数 */
  retryCount: number;
}

interface DistributablePackage {
  packageId: string;
  topic: string;
  hasScript: boolean;
  hasImage: boolean;
  hasVideo: boolean;
  targets: PublishTarget[];
}

// ============================================
// 平台配置
// ============================================

const PLATFORM_CONFIG: PublishTarget[] = [
  { platform: "wechat", label: "公众号", color: "#10B981", formatAdaptation: "图文格式，支持富文本，字数无限制", charLimit: 20000, selected: false, status: "pending", retryCount: 0 },
  { platform: "xiaohongshu", label: "小红书", color: "#EF4444", formatAdaptation: "图文+九宫格，标题20字，正文1000字", charLimit: 1000, selected: false, status: "pending", retryCount: 0 },
  { platform: "douyin", label: "抖音", color: "#000000", formatAdaptation: "短视频+文案，标题55字，文案300字", charLimit: 300, selected: false, status: "pending", retryCount: 0 },
  { platform: "bilibili", label: "B站", color: "#FB7299", formatAdaptation: "视频+专栏，标题80字，专栏20000字", charLimit: 20000, selected: false, status: "pending", retryCount: 0 },
  { platform: "zhihu", label: "知乎", color: "#3B82F6", formatAdaptation: "文章+回答，标题不限，正文无限制", charLimit: 100000, selected: false, status: "pending", retryCount: 0 },
];

const STATUS_META: Record<PublishStatus, { label: string; color: string; icon: typeof Clock }> = {
  pending: { label: "待发布", color: "#6B7280", icon: Clock },
  publishing: { label: "发布中", color: "#F59E0B", icon: Loader2 },
  success: { label: "已发布", color: "#10B981", icon: CheckCircle2 },
  failed: { label: "失败", color: "#EF4444", icon: XCircle },
  scheduled: { label: "已定时", color: "#3B82F6", icon: Calendar },
};

// ============================================
// 主组件
// ============================================

export default function ContentDistributor() {
  const [packages, setPackages] = useState<DistributablePackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [selectedPkgId, setSelectedPkgId] = useState<string | null>(null);
  const [scheduleTime, setScheduleTime] = useState<string>("");
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  // 加载可分发的包
  const loadPackages = useCallback(async () => {
    setLoading(true);
    try {
      if (RuntimeManager.isInitialized() && RuntimeManager.hasRuntime(RuntimeName.MediaStudio)) {
        const resp = await RuntimeManager.query(RuntimeName.MediaStudio, { type: "list_packages" });
        const inner = resp?.data as any;
        if (inner?.success && Array.isArray(inner.data)) {
          const pkgs = inner.data as MediaPackage[];
          const distributable: DistributablePackage[] = pkgs
            .filter((p) => p.status === "completed" || p.status === "partial")
            .map((p) => ({
              packageId: p.id,
              topic: p.topic || "未命名",
              hasScript: !!(p.articleScript || p.videoScript || p.xiaohongshuScript || p.bilibiliScript || p.douyinScript),
              hasImage: !!(p.cover || p.wechatCover || p.xiaohongshuGrid?.length || p.douyinCover),
              hasVideo: !!p.video,
              targets: PLATFORM_CONFIG.map((t) => ({ ...t })),
            }));
          setPackages(distributable);
          if (distributable.length > 0 && !selectedPkgId) {
            setSelectedPkgId(distributable[0].packageId);
          }
        }
      }
    } catch {
      setPackages([]);
    } finally {
      setLoading(false);
    }
  }, [selectedPkgId]);

  useEffect(() => {
    void loadPackages();
  }, [loadPackages]);

  // 当前选中的包
  const selectedPkg = useMemo(
    () => packages.find((p) => p.packageId === selectedPkgId) || null,
    [packages, selectedPkgId]
  );

  // 切换平台选择
  const togglePlatform = (platform: string) => {
    if (!selectedPkg) return;
    setPackages((prev) =>
      prev.map((p) =>
        p.packageId === selectedPkgId
          ? {
              ...p,
              targets: p.targets.map((t) =>
                t.platform === platform ? { ...t, selected: !t.selected } : t
              ),
            }
          : p
      )
    );
  };

  // 一键分发
  const distribute = useCallback(async () => {
    if (!selectedPkg) return;
    const targets = selectedPkg.targets.filter((t) => t.selected);
    if (targets.length === 0) {
      setMessage({ type: "err", text: "请至少选择一个发布平台" });
      return;
    }

    setPublishing(true);
    setMessage(null);

    try {
      // 逐个平台发布（模拟，实际需对接平台 API）
      for (const target of targets) {
        // 更新状态为发布中
        setPackages((prev) =>
          prev.map((p) =>
            p.packageId === selectedPkgId
              ? {
                  ...p,
                  targets: p.targets.map((t) =>
                    t.platform === target.platform ? { ...t, status: "publishing" } : t
                  ),
                }
              : p
          )
        );

        // 调用 MediaStudio publish_package（标记已发布）
        try {
          if (RuntimeManager.isInitialized()) {
            await RuntimeManager.execute(
              RuntimeName.MediaStudio,
              "publish_package",
              { packageId: selectedPkgId, platform: target.platform }
            );
          }
          // 模拟发布耗时
          await new Promise((r) => setTimeout(r, 500 + Math.random() * 500));

          // 发布成功
          setPackages((prev) =>
            prev.map((p) =>
              p.packageId === selectedPkgId
                ? {
                    ...p,
                    targets: p.targets.map((t) =>
                      t.platform === target.platform
                        ? { ...t, status: "success", publishedAt: new Date().toISOString() }
                        : t
                    ),
                  }
                : p
            )
          );
        } catch (err) {
          // 发布失败
          setPackages((prev) =>
            prev.map((p) =>
              p.packageId === selectedPkgId
                ? {
                    ...p,
                    targets: p.targets.map((t) =>
                      t.platform === target.platform
                        ? { ...t, status: "failed", error: (err as Error).message, retryCount: t.retryCount + 1 }
                        : t
                    ),
                  }
                : p
            )
          );
        }
      }

      setMessage({
        type: "ok",
        text: `✅ 分发完成：${targets.length} 个平台\n${targets.filter((t) => selectedPkg.targets.find((tt) => tt.platform === t.platform)?.status === "success").length} 成功`,
      });
    } catch (err) {
      setMessage({ type: "err", text: `❌ 分发失败：${(err as Error).message}` });
    } finally {
      setPublishing(false);
    }
  }, [selectedPkg]);

  // 重试失败的平台
  const retryPlatform = useCallback(async (platform: string) => {
    if (!selectedPkg) return;
    setPackages((prev) =>
      prev.map((p) =>
        p.packageId === selectedPkgId
          ? {
              ...p,
              targets: p.targets.map((t) =>
                t.platform === platform ? { ...t, status: "pending", error: undefined } : t
              ),
            }
          : p
      )
    );
  }, [selectedPkg, selectedPkgId]);

  // 统计
  const stats = useMemo(() => {
    if (!selectedPkg) return { total: 0, success: 0, failed: 0, pending: 0 };
    const targets = selectedPkg.targets;
    return {
      total: targets.length,
      success: targets.filter((t) => t.status === "success").length,
      failed: targets.filter((t) => t.status === "failed").length,
      pending: targets.filter((t) => t.status === "pending" || t.status === "publishing").length,
    };
  }, [selectedPkg]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />

      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-blue-500/15 border border-blue-500/30">
            <Send size={13} className="text-blue-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">一键多平台分发</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Distributor · {packages.length} 个可分发包 · {stats.success} 已发布 · {stats.failed} 失败
            </p>
          </div>
        </div>
        <button
          onClick={loadPackages}
          disabled={loading}
          className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] transition-colors disabled:opacity-50"
        >
          {loading ? <Loader2 size={11} className="text-gray-400 animate-spin" /> : <RefreshCw size={11} className="text-gray-400" />}
        </button>
      </div>

      {message && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div className={`px-3 py-2 rounded-md border text-[10px] font-mono whitespace-pre-line ${message.type === "ok" ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300" : "border-red-500/30 bg-red-500/[0.06] text-red-300"}`}>
            {message.text}
          </div>
        </div>
      )}

      <div className="p-4 space-y-3">
        {/* 包选择器 */}
        {packages.length === 0 ? (
          <div className="text-center py-8">
            <Send size={24} className="mx-auto text-gray-600 mb-2" />
            <p className="text-[11px] font-mono text-gray-500">暂无可分发内容</p>
            <p className="text-[9px] font-mono text-gray-700 mt-1">生成并完成内容后可一键分发</p>
          </div>
        ) : (
          <>
            <div>
              <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1.5 block">选择内容包</span>
              <select
                value={selectedPkgId || ""}
                onChange={(e) => setSelectedPkgId(e.target.value)}
                className="w-full bg-white/[0.04] border border-white/[0.06] rounded-md px-2 py-1.5 text-[11px] font-mono text-gray-200"
              >
                {packages.map((p) => (
                  <option key={p.packageId} value={p.packageId}>
                    {p.topic}（脚本:{p.hasScript ? "✓" : "✗"} 图片:{p.hasImage ? "✓" : "✗"} 视频:{p.hasVideo ? "✓" : "✗"}）
                  </option>
                ))}
              </select>
            </div>

            {/* 平台选择 */}
            {selectedPkg && (
              <div>
                <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1.5 block">选择发布平台</span>
                <div className="grid grid-cols-5 gap-2">
                  {selectedPkg.targets.map((target) => {
                    const sMeta = STATUS_META[target.status];
                    const SIcon = sMeta.icon;
                    const tColor = target.color === "#000000" ? "#9CA3AF" : target.color;
                    return (
                      <button
                        key={target.platform}
                        onClick={() => target.status === "pending" && togglePlatform(target.platform)}
                        className={`relative rounded-md border p-2 transition-all ${
                          target.selected ? "ring-1" : ""
                        } ${target.status !== "pending" ? "cursor-default" : "hover:bg-white/[0.04]"}`}
                        style={{
                          borderColor: `${tColor}30`,
                          background: target.selected ? `${tColor}10` : "rgba(255,255,255,0.02)",
                          boxShadow: target.selected ? `0 0 0 1px ${tColor}40` : undefined,
                        }}
                      >
                        <div className="text-center">
                          <div className="text-[10px] font-mono font-bold" style={{ color: tColor }}>
                            {target.label}
                          </div>
                          {target.status !== "pending" ? (
                            <div className="flex items-center justify-center gap-0.5 mt-1">
                              <SIcon size={9} className={target.status === "publishing" ? "animate-spin" : ""} style={{ color: sMeta.color }} />
                              <span className="text-[8px] font-mono" style={{ color: sMeta.color }}>{sMeta.label}</span>
                            </div>
                          ) : target.selected ? (
                            <div className="text-[8px] font-mono text-emerald-400 mt-1">✓ 已选</div>
                          ) : (
                            <div className="text-[8px] font-mono text-gray-600 mt-1">点击选择</div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 格式适配说明 */}
            {selectedPkg && (
              <div className="rounded-md border border-white/[0.04] bg-white/[0.01] p-2">
                <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">格式适配</span>
                <div className="space-y-1 mt-1">
                  {selectedPkg.targets.filter((t) => t.selected).map((t) => (
                    <div key={t.platform} className="flex items-start gap-1.5">
                      <span className="text-[9px] font-mono flex-shrink-0" style={{ color: t.color === "#000000" ? "#9CA3AF" : t.color }}>
                        {t.label}：
                      </span>
                      <span className="text-[9px] font-mono text-gray-500">{t.formatAdaptation}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 定时发布 */}
            <div className="flex items-center gap-2">
              <Calendar size={11} className="text-gray-500" />
              <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">定时发布：</span>
              <input
                type="datetime-local"
                value={scheduleTime}
                onChange={(e) => setScheduleTime(e.target.value)}
                className="bg-white/[0.04] border border-white/[0.06] rounded text-[10px] font-mono text-gray-300 px-2 py-0.5"
              />
              {scheduleTime && (
                <button onClick={() => setScheduleTime("")} className="text-[9px] font-mono text-gray-600 hover:text-gray-400">
                  清除
                </button>
              )}
            </div>

            {/* 发布按钮 */}
            <button
              onClick={distribute}
              disabled={publishing || !selectedPkg?.targets.some((t) => t.selected && t.status === "pending")}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-blue-500/15 border border-blue-500/30 hover:bg-blue-500/25 transition-colors disabled:opacity-50"
            >
              {publishing ? (
                <Loader2 size={12} className="text-blue-400 animate-spin" />
              ) : (
                <Send size={12} className="text-blue-400" />
              )}
              <span className="text-[11px] font-mono text-blue-300 uppercase tracking-wider">
                {publishing ? "发布中..." : scheduleTime ? `定时发布到 ${selectedPkg?.targets.filter((t) => t.selected).length || 0} 个平台` : `立即发布到 ${selectedPkg?.targets.filter((t) => t.selected).length || 0} 个平台`}
              </span>
            </button>

            {/* 发布状态列表 */}
            {selectedPkg && selectedPkg.targets.some((t) => t.status !== "pending") && (
              <div className="rounded-md border border-white/[0.04] bg-white/[0.01] p-2">
                <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">发布状态</span>
                <div className="space-y-1 mt-1">
                  {selectedPkg.targets.filter((t) => t.status !== "pending").map((t) => {
                    const sMeta = STATUS_META[t.status];
                    const SIcon = sMeta.icon;
                    return (
                      <div key={t.platform} className="flex items-center gap-2 p-1.5 rounded border border-white/[0.03] bg-white/[0.01]">
                        <SIcon size={10} className={t.status === "publishing" ? "animate-spin" : ""} style={{ color: sMeta.color }} />
                        <span className="text-[10px] font-mono" style={{ color: t.color === "#000000" ? "#9CA3AF" : t.color }}>{t.label}</span>
                        <span className="text-[9px] font-mono" style={{ color: sMeta.color }}>{sMeta.label}</span>
                        {t.publishedAt && (
                          <span className="text-[8px] font-mono text-gray-600 ml-auto">
                            {new Date(t.publishedAt).toLocaleString("zh-CN", { hour12: false })}
                          </span>
                        )}
                        {t.status === "failed" && (
                          <button
                            onClick={() => retryPlatform(t.platform)}
                            className="ml-auto px-1.5 py-0.5 rounded text-[8px] font-mono uppercase bg-amber-500/15 border border-amber-500/30 text-amber-300 hover:bg-amber-500/25 transition-colors"
                          >
                            重试
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </motion.div>
  );
}
